import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Package, Loader2 } from "lucide-react";
import { photoThumb, photoThumbBackend, photoFull } from "@/lib/photoUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 2026-06 · Public product share link — /p/:token (add ?embed=1 for the iframe version).
 * Content is ENGLISH ONLY (user spec). Anonymous visitors see item number, photo and
 * description; signed-in (registered) users additionally see USD prices already
 * multiplied by their personal price coefficient.
 *
 * 2026-10-08 · Removed Grid/List toggle (user requested grid-only). Container is
 * fluid (no max-w cap) so it stretches to whatever width the host site / iframe
 * gives it — fixes "宽度不够" when embedded in a wider page layout.
 */
export default function PublicProductLink() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/product-links/${token}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This link is not available"));
  }, [token]);

  const member = data?.view === "member";

  const products = useMemo(() => data?.products || [], [data]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6">
        <p className="text-sm text-neutral-600" data-testid="plink-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="animate-spin text-neutral-400" size={22} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-neutral-50 ${embed ? "p-3" : "p-4 sm:p-8"}`} data-testid="plink-page">
      {!embed && (
        <header className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Product Selection</p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-neutral-900" data-testid="plink-title">
            {data.title || "Products"}
          </h1>
          <p className="mt-1 text-xs text-neutral-500" data-testid="plink-meta">
            {data.count} item{data.count === 1 ? "" : "s"}
            {member ? " · prices in USD" : " · sign in to see prices"}
          </p>
        </header>
      )}

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="plink-grid">
        {products.map((p) => (
          <li key={p.item_number} className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
            data-testid={`plink-card-${p.item_number}`}>
            <Photo p={p} className="w-full h-56" />
            <div className="p-3">
              <p className="font-mono text-sm font-bold text-neutral-900">{p.item_number}</p>
              {p.description && <p className="mt-1 text-xs text-neutral-600">{p.description}</p>}
              {p.specification && <p className="mt-1 text-[11px] text-neutral-500">{p.specification}</p>}
              {member && <PriceRows rows={p.price_rows} item={p.item_number} />}
            </div>
          </li>
        ))}
      </ul>

      {!member && (
        <p className="mt-6 text-center text-[11px] text-neutral-500" data-testid="plink-signin-hint">
          Sign in with your account to view USD pricing.
        </p>
      )}
    </div>
  );
}

const Photo = ({ p, className }) => (
  p.photo_url ? (
    <a href={photoFull(p.photo_url)} target="_blank" rel="noreferrer" className={`block bg-white ${className}`}>
      <img src={photoThumb(p.photo_url, "md")} alt={p.item_number}
        className="w-full h-full object-contain"
        onError={(e) => {
          const i = e.currentTarget;
          if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(p.photo_url, "md"); }
        }}
        data-testid={`plink-photo-${p.item_number}`} />
    </a>
  ) : (
    <div className={`flex items-center justify-center bg-neutral-100 ${className}`}>
      <Package size={24} className="text-neutral-400" />
    </div>
  )
);

const PriceRows = ({ rows, item }) => {
  const list = (rows || []).filter((r) => r.size || r.price);
  if (!list.length) return null;
  return (
    <table className="mt-2 w-full table-fixed text-[11px]" data-testid={`plink-prices-${item}`}>
      <colgroup>
        <col style={{ width: "52%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "30%" }} />
      </colgroup>
      <thead>
        <tr className="text-neutral-500">
          <th className="text-left font-medium">Size</th>
          <th className="text-right font-medium">PCS/CTN</th>
          <th className="text-right font-medium">Price (USD)</th>
        </tr>
      </thead>
      <tbody>
        {list.map((r, i) => (
          <tr key={i} className="border-t border-neutral-100">
            <td className="py-0.5 pr-1 font-mono break-all">{r.size || "-"}</td>
            <td className="py-0.5 text-right font-mono">{r.qty_per_ctn || "-"}</td>
            <td className="py-0.5 text-right font-mono font-semibold whitespace-nowrap">${Number(r.price || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
